import React, { useState, useEffect } from 'react';
import './App.css';
import Backoffice from './Backoffice';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';

function resolveEventDomain() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_EVENT_DOMAIN || '';
  }
  return hostname.replace(/^www\./, '');
}
const EVENT_DOMAIN = resolveEventDomain();

function App() {
  const [step, setStep] = useState(0);
  const [attendance, setAttendance] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [guests, setGuests] = useState(1);
  const [children, setChildren] = useState(0);
  const [food, setFood] = useState([]);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [eventConfig, setEventConfig] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);

  useEffect(() => {
    const fetchEventConfig = async () => {
      try {
        const res = await fetch(`${SERVER_URL}/api/event/${EVENT_DOMAIN}`);
        if (!res.ok) throw new Error('Event not found');
        const data = await res.json();
        setEventConfig(data);
      } catch (err) {
        console.error('Failed to load event config', err);
      } finally {
        setEventLoading(false);
      }
    };
    if (EVENT_DOMAIN) {
      fetchEventConfig();
    } else {
      setEventLoading(false);
    }
  }, []);

  const handleAttendance = (option) => {
    setAttendance(option.value);
    if (option.declining) {
      setStep(4);
    } else {
      setStep(2);
    }
  };

  const handleFoodChange = (option) => {
    setFood((prev) =>
      prev.includes(option)
        ? prev.filter((f) => f !== option)
        : [...prev, option]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('');
    const selectedOption = eventConfig?.attendanceOptions?.find(o => o.value === attendance);
    const isDecl = selectedOption?.declining;
    try {
      const response = await fetch(`${SERVER_URL}/api/rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventDomain: EVENT_DOMAIN,
          name,
          phone,
          attendance,
          guests: isDecl ? 0 : guests,
          children: isDecl ? 0 : children,
          food,
          message
        })
      });
      const data = await response.json();
      if (response.ok) {
        setStatus('תודה! ההרשמה התקבלה בהצלחה.');
        setStep(5);
      } else {
        setStatus(data.error || 'אירעה שגיאה.');
      }
    } catch (err) {
      setStatus('אירעה שגיאה בשליחת הטופס.');
    }
    setLoading(false);
  };

  const isValidName = (val) => val && val.trim().length > 1;
  const isValidPhone = (val) => /^0[\d-]{8,10}$/.test(val);

  // Instead of early return, use conditional rendering
  const isBackoffice = window.location.pathname === '/backoffice';

  if (eventLoading) {
    return (
      <div className="App" dir="rtl">
        <div style={{ textAlign: 'center', padding: '40px' }}>טוען...</div>
      </div>
    );
  }

  return (
    <div className={`App ${isBackoffice ? 'backoffice-mode' : ''}`} dir="rtl">
      {/* Google Fonts for Noto Sans Hebrew */}
      <link
        href="https://fonts.googleapis.com/css2?family=Noto+Sans+Hebrew:wght@400;700&display=swap"
        rel="stylesheet"
      />
      {isBackoffice ? (
        <Backoffice eventConfig={eventConfig} />
      ) : (
        <form className="rsvp-form" onSubmit={handleSubmit}>
          {step === 0 && (
            <div className="welcome-container">
              <img
                src={eventConfig?.imageUrl || '/ido.png'}
                alt={eventConfig?.title || ''}
                className="ido-photo"
              />
              <div className="welcome-text">
                <h1 className="ido-title">{eventConfig?.title}</h1>
                <h2 className="ido-subtitle">{eventConfig?.subtitle}</h2>
                <div className="ido-date">
                  <b>{eventConfig?.dateDisplay}</b>
                  <br />
                </div>
                <div className="ido-event-details">
                  {eventConfig?.venues?.map((venue, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <br />}
                      {venue.header && (
                        <><span><strong>{venue.header}</strong></span><br /></>
                      )}
                      {venue.displayLines?.map((line, j) => (
                        <React.Fragment key={j}><span>{line}</span><br /></React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
                <div className="ido-family">נשמח לראותכם!<br /> {eventConfig?.familyName}</div>
              </div>
            </div>
          )}
          {step === 0 && (
            <button
              type="button"
              onClick={() => setStep(1)}
              className="rsvp-btn rsvp-btn-main"
            >
              🎉 המשך לאישור הגעה
            </button>
          )}
          {step === 1 && (
            <>
              <h3 className="rsvp-question">האם תגיעו?</h3>
              <div className="rsvp-btn-group">
                {(eventConfig?.attendanceOptions || []).map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleAttendance(option)}
                    className="rsvp-btn"
                  >
                    {option.emoji} {option.label}
                  </button>
                ))}
              </div>
              <div className="rsvp-btn-group rsvp-btn-group-back">
                <button
                  type="button"
                  onClick={() => setStep(0)}
                  className="rsvp-btn rsvp-btn-back"
                >
                  ➡️ חזור
                </button>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <div className="rsvp-field">
                <label className="rsvp-label">שם מלא</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  className="rsvp-input"
                  placeholder="הכניסו את שמכם המלא"
                />
              </div>
              <div className="rsvp-field">
                <label className="rsvp-label">מספר טלפון</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  required
                  className="rsvp-input"
                  placeholder="הכניסו מספר טלפון"
                  pattern="^0[\d-]{8,10}$"                  
                  title="הכניסו מספר טלפון תקין"
                />
              </div>
              <div className="rsvp-field">
                <label className="rsvp-label">כמה מבוגרים מגיעים?</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={guests}
                  onChange={e => setGuests(Number(e.target.value))}
                  required
                  className="rsvp-input"
                />
              </div>
              <div className="rsvp-field">
                <label className="rsvp-label">כמה ילדים (בגיל 2-7)?</label>
                <input
                  type="number"
                  min="0"
                  max={guests}
                  value={children}
                  onChange={e => setChildren(Number(e.target.value))}
                  required
                  className="rsvp-input"
                />
              </div>
              <div className="rsvp-btn-group1">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="rsvp-btn rsvp-btn-back"
                  style={{ order: 1 }}
                >
                  ➡️ חזור
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!isValidName(name)) {
                      setStatus('אנא הזינו שם מלא תקין');
                      return;
                    }
                    if (!isValidPhone(phone)) {
                      setStatus('אנא הזינו מספר טלפון תקין');
                      return;
                    }
                    setStatus('');
                    setStep(3);
                  }}
                  className="rsvp-btn"
                  style={{ order: 2 }}
                >
                  המשך ⬅️
                </button>
              </div>
              {status && (
                <div className={`rsvp-status ${status.includes('תודה') ? 'rsvp-status-success' : 'rsvp-status-error'}`}>
                  {status}
                </div>
              )}
            </>
          )}
          {step === 3 && (
            <>
              <div className="rsvp-field">
                <label className="rsvp-label">העדפות תפריט (ניתן לבחור יותר מאחת):</label>
                {(eventConfig?.foodOptions || []).map(option => (
                  <div key={option} className="rsvp-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={food.includes(option)}
                        onChange={() => handleFoodChange(option)}
                        className="rsvp-checkbox-input"
                      />
                      {option}
                    </label>
                  </div>
                ))}
              </div>
              <div className="rsvp-field">
                <label className="rsvp-label">הודעה למארחים (לא חובה)</label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={3}
                  className="rsvp-input"
                  placeholder="הערות נוספות..."
                />
              </div>
              <div className="rsvp-btn-group1">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="rsvp-btn rsvp-btn-back"
                  style={{ order: 1 }}
                >
                  ➡️ חזור
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="rsvp-btn"
                  style={{ order: 2 }}
                >
                  {loading ? '⏳ שולח...' : '📤 שלח אישור הגעה'}
                </button>
              </div>
              {status && (
                <div className={`rsvp-status ${status.includes('תודה') ? 'rsvp-status-success' : 'rsvp-status-error'}`}>
                  {status}
                </div>
              )}
            </>
          )}
          {step === 4 && (
            <>
              <div className="rsvp-field">
                <label className="rsvp-label">שם מלא</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  className="rsvp-input"
                  placeholder="הכניסו את שמכם המלא"
                />
              </div>
              <div className="rsvp-field">
                <label className="rsvp-label">מספר טלפון</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  required
                  className="rsvp-input"
                  placeholder="הכניסו מספר טלפון"
                  pattern="^0[\d-]{8,10}$"                  
                  title="הכניסו מספר טלפון תקין"
                />
              </div>
              <div className="rsvp-field">
                <label className="rsvp-label">הודעה למארחים (לא חובה)</label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={3}
                  className="rsvp-input"
                  placeholder="הערות נוספות..."
                />
              </div>
              <div className="rsvp-btn-group">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="rsvp-btn rsvp-btn-back"
                  style={{ order: 1 }}
                >
                  ➡️ חזור
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="rsvp-btn"
                  style={{ order: 2 }}
                  onClick={e => {
                    if (!isValidName(name) || !isValidPhone(phone)) {
                      e.preventDefault();
                      setStatus(!isValidName(name)
                        ? 'אנא הזינו שם מלא תקין'
                        : 'אנא הזינו מספר טלפון תקין');
                    } else {
                      setStatus('');
                    }
                  }}
                >
                  {loading ? '⏳ שולח...' : '📤 שלח אישור הגעה'}
                </button>
              </div>
              {status && (
                <div className={`rsvp-status ${status.includes('תודה') ? 'rsvp-status-success' : 'rsvp-status-error'}`}>
                  {status}
                </div>
              )}
            </>
          )}
          {step === 5 && (
            <div className="rsvp-success-message">
              <img
                src={eventConfig?.imageUrl || '/ido.png'}
                alt={eventConfig?.title || ''}
                className="ido-photo-success"
              />
              <h2>תודה!</h2>
              ההרשמה התקבלה בהצלחה.<br />
              נשמח לראותכם!
            </div>
          )}
        </form>
      )}
    </div>
  );
}

export default App;
